/**
 * The arithmetic behind the clearance cross-section (the Profile tab).
 *
 * Pure and DOM-free so it is unit-testable without r3f or a canvas, in the
 * same split `lib/stationVolumes/sectionModel.ts` uses: this file is the
 * numbers, `ClearanceSectionChart` is the drawing and the interaction.
 *
 * What the section shows, and why it is a grid rather than a pair of
 * surfaces: a clearance run has no surface pair. What it has is a cloud, and
 * the question the section answers is "what is actually in the envelope at
 * this station, and how far in". A per-offset min/max band cannot answer that
 * — a wire above an empty gap and a solid wall both reduce to the same two
 * numbers. An occupancy grid draws what is there.
 */
import type { ClearanceSectionBins, ClearanceVertex } from './types';

/** The two planes of `<stem>_station_clearance_section.u16`, decoded. */
export interface ClearanceSection {
  /** Scored points per cell, `nStations * nHeights * nOffsets`, C order. */
  count: Uint16Array;
  /** The violating subset of `count`, same shape and order. */
  violations: Uint16Array;
  bins: ClearanceSectionBins;
}

/**
 * Decode the raw byproduct against its OWN descriptor.
 *
 * The length check is not ceremony. The one defect that cost this feature two
 * deploy cycles was a binary payload whose declared layout disagreed with its
 * bytes (the `Clearance` extra dimension, declared 16 bytes before where it
 * actually sat), and it was silent — the reader got plausible-looking zeros.
 * A grid decoded against the wrong shape is the same failure with the same
 * symptom: a section that looks like a section and is not this station's.
 */
export function decodeClearanceSection(buf: ArrayBuffer, bins: ClearanceSectionBins): ClearanceSection {
  const per = bins.nStations * bins.nHeights * bins.nOffsets;
  const expected = per * 2 * 2; // 2 planes, uint16
  if (buf.byteLength !== expected) {
    throw new Error(
      `${bins.file} is ${buf.byteLength} bytes, expected ${expected} for ` +
        `${bins.nStations}x${bins.nHeights}x${bins.nOffsets}x2 planes of uint16`,
    );
  }
  return {
    count: new Uint16Array(buf, 0, per),
    violations: new Uint16Array(buf, per * 2, per),
    bins,
  };
}

/** One station's two planes, as views — no copy, no allocation per frame. */
export function sectionCellsAt(section: ClearanceSection, stationIndex: number) {
  const { nStations, nHeights, nOffsets } = section.bins;
  // Clamp rather than throw: the walk can sit past the last bin while the
  // dock is open on a shorter run, and a section is a view, not a
  // measurement API. (The server-side twin of this rule throws instead —
  // deliberately, see CLAUDE.md on the per-station PDF.)
  const i = Math.max(0, Math.min(nStations - 1, stationIndex));
  const a = i * nHeights * nOffsets;
  const b = a + nHeights * nOffsets;
  return { count: section.count.subarray(a, b), violations: section.violations.subarray(a, b) };
}

/** Cell centre in metres, for axis labels and hover readouts. */
export const offsetAt = (bins: ClearanceSectionBins, col: number): number =>
  bins.offsetStartM + (col + 0.5) * bins.offsetStepM;
export const heightAt = (bins: ClearanceSectionBins, row: number): number =>
  bins.heightStartM + (row + 0.5) * bins.heightStepM;

/** Inverse, for turning a pointer position back into a cell. */
export const colAt = (bins: ClearanceSectionBins, offsetM: number): number =>
  Math.floor((offsetM - bins.offsetStartM) / bins.offsetStepM);
export const rowAt = (bins: ClearanceSectionBins, heightM: number): number =>
  Math.floor((heightM - bins.heightStartM) / bins.heightStepM);

/**
 * The busiest cell in one station, used to normalise the density ramp.
 *
 * Per-station, not per-run: station-to-station density varies by an order of
 * magnitude (a scan slows at a junction, a bin clips the end of the
 * alignment), and normalising the whole run to its densest station would draw
 * every other station as an almost-empty grid.
 */
export function peakCount(cells: Uint16Array): number {
  let max = 0;
  for (let i = 0; i < cells.length; i++) if (cells[i] > max) max = cells[i];
  return max;
}

/**
 * Density → 0..1 ink, on a LOG curve.
 *
 * Linear is unreadable here: a road surface cell holds thousands of returns
 * and a thin pole a handful, so on a linear ramp the pole — the thing you are
 * looking for — is invisible next to the tarmac. `log1p` keeps the sparse
 * structure legible while the dense structure still reads as solid.
 */
export function density(value: number, peak: number): number {
  if (value <= 0 || peak <= 0) return 0;
  return Math.log1p(value) / Math.log1p(peak);
}

export interface SectionViewport {
  /** Pixels per metre. ONE scale for both axes — see below. */
  scale: number;
  originX: number;
  originY: number;
  width: number;
  height: number;
}

/**
 * Fit the grid's extent into the pane at a SINGLE scale for both axes.
 *
 * Deliberately no vertical exaggeration, which is where this parts company
 * with the station-volumes section. There, Δz is centimetres across a corridor
 * tens of metres wide, so a stated exaggeration is the only way to see
 * anything. Here both axes are the same kind of quantity at the same order of
 * magnitude (a ±3.6 m by 8 m box), and the profile's SHAPE is the thing being
 * judged — a gauge drawn with independent axes is a lie about the gauge, which
 * is exactly what `ClearanceProfileSketch` says in its own comment.
 */
export function sectionViewport(
  bins: ClearanceSectionBins,
  widthPx: number,
  heightPx: number,
  padPx: number,
): SectionViewport {
  const spanX = Math.max(bins.nOffsets * bins.offsetStepM, 1e-6);
  const spanY = Math.max(bins.nHeights * bins.heightStepM, 1e-6);
  const usableW = Math.max(1, widthPx - 2 * padPx);
  const usableH = Math.max(1, heightPx - 2 * padPx);
  const scale = Math.min(usableW / spanX, usableH / spanY);
  return {
    scale,
    // Centre the drawing in whatever the pane left over.
    originX: padPx + (usableW - spanX * scale) / 2,
    originY: padPx + (usableH - spanY * scale) / 2,
    width: spanX * scale,
    height: spanY * scale,
  };
}

/** Metres → pixels. Y is flipped: height grows upward, screen Y grows down. */
export const toPxX = (v: SectionViewport, bins: ClearanceSectionBins, offsetM: number): number =>
  v.originX + (offsetM - bins.offsetStartM) * v.scale;
export const toPxY = (v: SectionViewport, bins: ClearanceSectionBins, heightM: number): number =>
  v.originY + v.height - (heightM - bins.heightStartM) * v.scale;

/** The profile polygon as an SVG points string in the same viewport. */
export function profilePolygonPoints(
  vertices: readonly ClearanceVertex[],
  v: SectionViewport,
  bins: ClearanceSectionBins,
): string {
  return vertices.map(([o, h]) => `${toPxX(v, bins, o).toFixed(2)},${toPxY(v, bins, h).toFixed(2)}`).join(' ');
}

/**
 * Nice tick values covering `[start, start + span]` at a sensible step.
 *
 * Chosen from the SCALE, not from a fixed count: at 4 px/m a tick per metre is
 * a smear, and at 60 px/m a tick every 5 m is two ticks on the whole axis.
 */
export function sectionTicks(startM: number, spanM: number, pxPerM: number): number[] {
  const targetPx = 56;
  const rawStep = targetPx / Math.max(pxPerM, 1e-6);
  const steps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50];
  const step = steps.find((s) => s >= rawStep) ?? steps[steps.length - 1];
  const first = Math.ceil(startM / step) * step;
  const out: number[] = [];
  for (let v = first; v <= startM + spanM + 1e-9; v += step) {
    // Kill float drift so a tick reads "0" rather than "-1.1e-16".
    out.push(Math.abs(v) < 1e-9 ? 0 : Number(v.toFixed(6)));
  }
  return out;
}
